import _ from 'lodash'
import { lazy, Suspense } from 'react'
import { put, call } from 'redux-saga/effects'

import { _DB, pouchDbFactoryFn } from 'core/data'
import { createBasicPlanRepository } from 'core/data-model/basic-plan/repository'
import { createCampaignRepository } from 'core/data-model/campaign/repository'
import { createDistributorGroupRepository } from 'core/data-model/distributor/repository'
import { createFNAConfigurationRepository } from 'core/data-model/fna/repository'
import { createInvestmentRepository } from 'core/data-model/investment/repository'
import { createLoanRepository } from 'core/data-model/loan/repository'
import { createRiderRepository } from 'core/data-model/rider/repository'
import { createBasicPlanService } from 'core/service/basic-plan/basic-plan'
import { createBasicPlanPremiumService } from 'core/service/basic-plan/premium'
import { createBasicPlanRuleValidation } from 'core/service/basic-plan/rules/rules-validator'
import { createBasicPlanTaxRate } from 'core/service/basic-plan/tax-rate'
import { createBasicPlanValidationService } from 'core/service/basic-plan/validation'
import { createBenefitIllustrationPolicyValueService } from 'core/service/benefit-illustration/policy-value'
import { createBenefitIllustrationYearEndRiderPremiumService } from 'core/service/benefit-illustration/year-end-rider-premium'
import { createDisplayProductService } from 'core/service/display-product/display-product'
import { createInsuredOccupationService } from 'core/service/insured/occupation'
import { createInsuredValidateService } from 'core/service/insured/validation'
import { createInvestmentCashFlowService } from 'core/service/investment/cash-flow/cash-flow'
import { createInvestmentCoverageLumpsumService } from 'core/service/investment/coverage/lumpsum'
import { createInvestmentCoverageValidateService } from 'core/service/investment/coverage/validation'
import { createInvestmentFundService } from 'core/service/investment/funds'
import { getQuestionnaire, getQuestionnaireV2 } from 'core/service/investment/questionnaire'
import { createLoanDocumentListService } from 'core/service/loan/loan-document-list'
import { createLoanPremiumService } from 'core/service/loan/premium'
import { createProductInvestmentPdfDocumentFunc } from 'core/service/pdf-generation/products/investment/pdf-document'
import { createRiderPremiumRateService } from 'core/service/rider/premium-rate'
import { createRiderService } from 'core/service/rider/rider'
import { createRiderTaxRateService } from 'core/service/rider/tax-rate'
import * as onRequest from 'core/service-wrapper/legacy/handler/on-request'
import { getAppConfig } from 'deploy-env/app-config'
import { getToggles } from 'quick-quote/feature-toggles'
import { createInsuredInformationFunc } from 'quick-quote/insured-information/sagas'
import * as insuredInformationSaga from 'quick-quote/insured-information/sagas'
import * as insuredInformationSelector from 'quick-quote/insured-information/selectors'
import * as productBumnanReadyCoveragePlanSelector from 'quick-quote/product-bumnan-ready/coverage-plan/selectors'
import { createProductBumnanReadyFunc } from 'quick-quote/product-bumnan-ready/sagas'
import { createProductCommonBenefitFunc } from 'quick-quote/product-common/benefit-illustration/sagas'
import { createProductCommonCoverageFunc } from 'quick-quote/product-common/coverage-plan/sagas'
import { createProductHealthFunc } from 'quick-quote/product-health/coverage-plan/sagas'
import * as productHealthToppingCoveragePlanSelector from 'quick-quote/product-health-topping/coverage-plan/selectors'
import { createProductHealthToppingFunc } from 'quick-quote/product-health-topping/sagas'
import { createProductIHealthyUltraCoverageFunc } from 'quick-quote/product-ihealthy-ultra/coverage-plan/sagas'
import * as productIHealthyUltraCoverageSelector from 'quick-quote/product-ihealthy-ultra/coverage-plan/selectors'
import { createProductIHealthyUltraFunc } from 'quick-quote/product-ihealthy-ultra/sagas'
import { createProductInvestmentBenefitFunc } from 'quick-quote/product-investment/benefit-illustration/sagas'
import { createProductInvestmentCoverageFunc } from 'quick-quote/product-investment/coverage-plan/sagas'
import { updateQuestionnaire } from 'quick-quote/product-investment/risk-questionnaire/actions'
import { createProductLifeReadyFunc } from 'quick-quote/product-life-ready/sagas'
import { createProductLifeSaveProBenefitsFunc } from 'quick-quote/product-life-save-pro/benefit-illustration/sagas'
import { createProductLifeSaveProFunc } from 'quick-quote/product-life-save-pro/sagas'
import { createProductLoanBenefitFunc } from 'quick-quote/product-loan/benefit-illustration/sagas'
import { createProductLoanCoverageFunc } from 'quick-quote/product-loan/coverage-plan/redux'
import { createProductMRTAFunc } from 'quick-quote/product-mrta/sagas'
import * as productPACoverageSelector from 'quick-quote/product-pa/coverage-plan/selectors'
import { createProductPAFunc } from 'quick-quote/product-pa/sagas'
import { createProductPerfectLifeFunc } from 'quick-quote/product-perfect-life/sagas'
import { selectProduct } from 'quick-quote/product-selection/actions'
import { createProductSIOFunc } from 'quick-quote/product-sio/sagas'
import { createProductSukhapabDekDeeFunc } from 'quick-quote/product-sukhapabdekdee/sagas'
import { createProductTermLifeBenefitFunc } from 'quick-quote/product-term-life/benefit-illustration/sagas'
import * as productTermLifeCoverageSelector from 'quick-quote/product-term-life/coverage-plan/selectors'
import { crateProductTermLifeFunc } from 'quick-quote/product-term-life/sagas'
import { createProductWholeLifeBenefitFunc } from 'quick-quote/product-whole-life/benefit-illustration/sagas'
import { createProductWholeLifeFunc } from 'quick-quote/product-whole-life/sagas'

export async function createLegacyService() {
  const dbWrapper = _DB(pouchDbFactoryFn())

  // repository
  const riderRepository = createRiderRepository(dbWrapper)
  const basicPlanRepository = createBasicPlanRepository(dbWrapper, riderRepository)
  const campaignRepository = createCampaignRepository(dbWrapper, basicPlanRepository, riderRepository)
  const distributorRepository = createDistributorGroupRepository(dbWrapper)
  const fnaRepository = createFNAConfigurationRepository(dbWrapper)
  const investmentRepository = createInvestmentRepository(dbWrapper)
  const loanRepository = createLoanRepository(dbWrapper)

  // service
  const displayProductService = createDisplayProductService(basicPlanRepository, campaignRepository)
  const insuredOccupationService = createInsuredOccupationService(dbWrapper, displayProductService)
  const insuredValidationService = createInsuredValidateService(displayProductService, insuredOccupationService)
  const riderService = await createRiderService(displayProductService, insuredOccupationService)
  const riderTaxRateService = createRiderTaxRateService(dbWrapper, insuredOccupationService, riderService)
  const riderPremiumRateService = createRiderPremiumRateService(
    dbWrapper,
    displayProductService,
    insuredOccupationService,
    riderService
  )
  const basicPlanPremiumService = createBasicPlanPremiumService(
    dbWrapper,
    displayProductService,
    riderService,
    insuredOccupationService
  )
  const basicPlanValidationService = createBasicPlanValidationService(displayProductService, insuredOccupationService)
  const basicPlanService = createBasicPlanService(
    dbWrapper,
    displayProductService,
    insuredOccupationService,
    riderService,
    basicPlanPremiumService,
    basicPlanValidationService,
    riderPremiumRateService
  )
  const basicPlanTaxRateService = createBasicPlanTaxRate(dbWrapper)
  const basicPlanRuleValidationService = createBasicPlanRuleValidation(basicPlanRepository)
  const benefitIllustrationYearEndRiderPremiumService = createBenefitIllustrationYearEndRiderPremiumService(
    riderService,
    riderPremiumRateService
  )
  const benefitIllustrationPolicyValueService = createBenefitIllustrationPolicyValueService(
    dbWrapper,
    benefitIllustrationYearEndRiderPremiumService,
    displayProductService,
    insuredOccupationService
  )
  const investmentCashFlowService = createInvestmentCashFlowService(investmentRepository)
  const investmentCoverageLumpsumService = createInvestmentCoverageLumpsumService(dbWrapper)
  const investmentCoverageValidationService = createInvestmentCoverageValidateService(dbWrapper, displayProductService)
  const investmentFundService = createInvestmentFundService(investmentRepository)
  const loanPremiumService = createLoanPremiumService(dbWrapper, basicPlanPremiumService)
  const loanDocumentListService = createLoanDocumentListService(loanRepository)

  // wrapper function
  const insuredInformationFunc = createInsuredInformationFunc(insuredValidationService, riderService)
  const productInvestmentCoverageFunc = createProductInvestmentCoverageFunc(
    dbWrapper,
    insuredOccupationService,
    investmentCoverageValidationService,
    investmentCashFlowService,
    investmentCoverageLumpsumService
  )
  const productInvestmentBenefitFunc = createProductInvestmentBenefitFunc(dbWrapper)
  const productInvestmentPdfDocumentFunc = createProductInvestmentPdfDocumentFunc(investmentCashFlowService)
  const productCommonCoverageFunc = createProductCommonCoverageFunc(
    basicPlanService,
    basicPlanPremiumService,
    basicPlanValidationService,
    riderService,
    riderPremiumRateService,
    displayProductService,
    insuredOccupationService,
    investmentCoverageValidationService
  )
  const productCommonBenefitFunc = createProductCommonBenefitFunc()
  const productHealthFunc = createProductHealthFunc()
  const productHealthToppingFunc = createProductHealthToppingFunc()
  const productIHealthyUltraFunc = createProductIHealthyUltraFunc(productCommonCoverageFunc)
  const productIHealthyUltraCoverageFunc = createProductIHealthyUltraCoverageFunc(
    basicPlanService,
    productCommonCoverageFunc
  )
  const productLifeReadyFunc = createProductLifeReadyFunc(productCommonCoverageFunc)
  const productLifeSaveProFunc = createProductLifeSaveProFunc(productCommonCoverageFunc)
  const productLifeSaveProBenefitFunc = createProductLifeSaveProBenefitsFunc()
  const productLoanCoverageFunc = createProductLoanCoverageFunc(
    basicPlanRepository,
    basicPlanRuleValidationService,
    loanPremiumService
  )
  const productLoanBenefitFunc = createProductLoanBenefitFunc(loanDocumentListService)
  const productMRTAFunc = createProductMRTAFunc(basicPlanRepository, basicPlanService, productCommonCoverageFunc)
  const productPAFunc = createProductPAFunc(basicPlanRepository, riderPremiumRateService, productCommonCoverageFunc)
  const productTermLifeFunc = crateProductTermLifeFunc(productCommonCoverageFunc)
  const productTermLifeBenefitFunc = createProductTermLifeBenefitFunc()
  const productWholeLifeFunc = createProductWholeLifeFunc(productCommonCoverageFunc)
  const productWholeLifeBenefitFunc = createProductWholeLifeBenefitFunc(
    dbWrapper,
    basicPlanTaxRateService,
    benefitIllustrationPolicyValueService,
    riderTaxRateService
  )
  const productBumnanReadyFunc = createProductBumnanReadyFunc(productCommonCoverageFunc)
  const productPerfectLifeFunc = createProductPerfectLifeFunc(productCommonCoverageFunc)
  const productSukhapabdekdeeFunc = createProductSukhapabDekDeeFunc()
  const productSIOFunc = createProductSIOFunc(productCommonCoverageFunc)

  function isOrdinaryForceLoginProduct(displayProduct) {
    const { ORDINARY_PRODUCT_FORCE_LOGIN_AVALIABLE, toggles } = getAppConfig()
    return (
      toggles.ENABLE_FORCE_LOGIN_ORDINARY_PRODUCT &&
      ORDINARY_PRODUCT_FORCE_LOGIN_AVALIABLE.includes(displayProduct.code)
    )
  }

  // prettier-ignore
  return {
    isPartnerApi: false,
    checkApplicationDataReady: (usePartnerApi) => onRequest.checkApplicationDataReady(dbWrapper, usePartnerApi),
    isOrdinaryForceLoginProduct: isOrdinaryForceLoginProduct,

    getDBWrapper: () => dbWrapper,
    dump: {
      loadQuestionnaire: function* () {
        let questionnaire
        if (getToggles().ENABLE_RPQ_FORM_2022) {
          questionnaire = _.cloneDeep(getQuestionnaireV2())
          for (let i = 0; i < questionnaire.length; i++) {
            if (i === 6) {
              questionnaire[i].questionImage = 'assets/images/question7_pic2.png'
            }
          }
        } else {
          questionnaire = getQuestionnaire()
        }

        yield put(updateQuestionnaire(questionnaire))
      },
      loadDistributorGroups: function* () {
        yield call(onRequest.onGetDistributorGroupsRequest, distributorRepository)
      },
      loadOccupations: function* () {
        yield call(onRequest.onGetOccupationsRequest, dbWrapper)
      },
      loadOccupationFactors: function* () {
        yield call(onRequest.onGetOccupationFactorsRequest, dbWrapper)
      },
      loadProducts: function* () {
        return yield call(onRequest.onGetProductsRequest, displayProductService)
      },
      loadRiders: function* () {
        yield call(onRequest.onGetRidersRequest, riderRepository)
      },
      loadModelFactors: function* () {
        yield call(onRequest.onGetModelFactorsRequest, dbWrapper)
      },
      loadViewConfiguration: function* () {
        yield call(onRequest.onGetViewConfiguration, basicPlanRepository)
      },
      loadFNAConfiguration: function* () {
        yield call(onRequest.onGetFNAConfiguration, fnaRepository)
      },
    },

    insured: {
      getOccupations: insuredInformationSelector.getUniqueOccupations,
      onEditBirthdate: insuredInformationFunc.validateInsuredBirthdate,
      onSelectOccupation: insuredInformationSaga.updateNatureOfDuty,
      onSelectOtherOccupation: insuredInformationSaga.updateOtherNatureOfDuty,
      onSelectNatureOfDuty: insuredInformationFunc.validateOccupation,
      onSelectOtherNatureOfDuty: insuredInformationFunc.validateOtherOccupation,
    },
    payer: {
      getOccupations: insuredInformationSelector.getUniqueOccupations,
      onEditBirthdate: insuredInformationFunc.validatePayerBirthdate,
      onSelectOccupation: insuredInformationSaga.updatePayerNatureOfDuty,
      onSelectOtherOccupation: () => { },
      onSelectNatureOfDuty: insuredInformationFunc.validatePayerOccupation,
      onSelectOtherNatureOfDuty: insuredInformationFunc.validatePayerOtherOccupation,
      onUpdatePayerNeed: () => { },
    },

    getBasicPlan: displayProductService.getBasicPlan,
    getRiskiestOccupation: insuredOccupationService.getRiskiestOccupation,
    getRiskiestOccupationFactorForRiders: insuredOccupationService.getRiskiestOccupationFactorForRiders,
    renderGraph: (basicPlanCode, className) => {
      const renderGraphLazy = (filename) => {
        const Graph = lazy(() =>
          import(`assets/images/product-benefits/${filename}.svg`)
            .catch(() => {
              console.error(`Graph not found: ${filename}`)
              return { default: () => null }
            })
        )
        return <Graph />
      }

      return (
        <div className={className}>
          <Suspense fallback={null}>{(() => renderGraphLazy(`Graph-${basicPlanCode}`))()}</Suspense>
        </div>
      )
    },
    products: {
      COMMON: {
        onSelectProduct: function* (comingSelectedProduct) {
          yield put(selectProduct(comingSelectedProduct))
        },
        onCalculatePremium: productCommonCoverageFunc.onCalculatePremium,
        generateCoveragePlanFromPremiumChange: productCommonCoverageFunc.generateCoveragePlanFromPremiumChange,
        generateCoveragePlanFromSumAssuredChange: productCommonCoverageFunc.generateCoveragePlanFromSumAssuredChange,
        populateAvailableRiders: productCommonCoverageFunc.populateAvailableRiders,
        revalidateRiders: productCommonCoverageFunc.revalidateRiders,
        updateDefaultRiderSumAssured: productCommonCoverageFunc.updateDefaultRiderSumAssured,
        updateRiderPremium: productCommonCoverageFunc.updateRiderPremium,
        updateRiderSelectedPlan: productCommonCoverageFunc.updateRiderSelectedPlan,
        updateAllSelectedRiderPremium: productCommonCoverageFunc.updateAllSelectedRiderPremium,
        updateTotalPremiumState: productCommonCoverageFunc.updateTotalPremiumState,
        // CommonPDFFunc
        prepareBIProps: productCommonBenefitFunc.prepareBIProps,
        generatePDFToBase64: productCommonBenefitFunc.generatePDFToBase64,
        generateRaAndRpqForm: productCommonBenefitFunc.generateRaAndRpqForm,

        isUsedTMORate: riderPremiumRateService.isUsedTMORate,
      },
      BUMNAN_READY: {
        getSelectableProductBumnanReady: productBumnanReadyCoveragePlanSelector.getSelectableProductBumnanReady,
        onSelectProduct: productBumnanReadyFunc.onUpdateSelectedBumnanReady,
        switchBumnanReadyProductByAge: productBumnanReadyFunc.switchBumnanReadyProductByAge,
        handleUpdateBirthDateWithProductBumnan: productBumnanReadyFunc.handleUpdateBirthDateWithProductBumnan,
        switchBumnanSmartProductByPensionAge: productBumnanReadyFunc.switchBumnanSmartProductByPensionAge,
        switchKoomverRetireProductByPensionAge: productBumnanReadyFunc.switchKoomverRetireProductByPensionAge,
        switchBumnanSmartProduct: productBumnanReadyFunc.switchBumnanSmartProduct,
      },
      HEALTH: {
        onUpdateSelectedBasicPlan: productHealthFunc.onUpdateSelectedBasicPlan,
        onValidateBasicPlan: productHealthFunc.onValidateBasicPlan,
      },
      HEALTH_TOPPING: {
        onUpdateSelectedBasicPlan: productHealthToppingFunc.selectHealthToppingPlan,
        getHealthToppingOptionsPlans: productHealthToppingCoveragePlanSelector.getHealthToppingOptionsPlans,
      },
      I_HEALTHY_ULTRA: {
        switchIHealthyProductByBirthDate: productIHealthyUltraFunc.switchIHealthyProductByBirthDate,
        getBasicPlanOptions: productIHealthyUltraCoverageSelector.getBasicPlanOptions,
        getSelectedDisplayProductCodeIHealthyUltra:
          productIHealthyUltraCoverageSelector.getSelectedDisplayProductCodeIHealthyUltra,
        validateProductMultiBasicPlanCode: productIHealthyUltraCoverageFunc.validateProductMultiBasicPlanCode,
        onUpdateSelectedBasicPlan: productIHealthyUltraCoverageFunc.onUpdateSelectedBasicPlan,
        generateCoveragePlanFromBasicPlanChange:
          productIHealthyUltraCoverageFunc.generateCoveragePlanFromBasicPlanChange,
        onValidateBasicPlan: productIHealthyUltraCoverageFunc.onValidateBasicPlan,
      },
      INVESTMENT: {
        // fund-allocation
        getFundLimit: investmentRepository.getInvestmentFundLimits,
        getSellableFunds: investmentFundService.getSellableFunds,
        // coverage
        getCashFlowProps: productInvestmentCoverageFunc.getCashFlowProps,
        onEditRegularTopUp: productInvestmentCoverageFunc.onEditRegularTopUp,
        onEditSumAssured: productInvestmentCoverageFunc.onEditSumAssured,
        onEditLumpSum: productInvestmentCoverageFunc.onEditLumpSum,
        onUpdateRegularPremium: productInvestmentCoverageFunc.onUpdateRegularPremium,
        doUpdateExpectedReturnComparisons: productInvestmentCoverageFunc.doUpdateExpectedReturnComparisons,
        revalidateSumAssured: productInvestmentCoverageFunc.revalidateSumAssured,
        // benefit
        addOrRemoveRiderBenefits: productInvestmentBenefitFunc.addOrRemoveRiderBenefits,
        isUsedCoiTMORate: investmentCashFlowService.isUsedCoiTMORate,
        generateCashFlow: investmentCashFlowService.generateCashFlow,
        // pdf-document
        generateCashFlowByExpectedReturn: productInvestmentPdfDocumentFunc._generateCashFlowByExpectedReturn,
      },
      LIFE_READY: {
        onSelectProduct: productLifeReadyFunc.onSelectProduct,
        onUpdateSelectedLifeReady: productLifeReadyFunc.onUpdateSelectedLifeReady,
        onUpdateSelectedLifeTreasure: productLifeReadyFunc.onUpdateSelectedLifeTreasure,
        onUpdateSelectedCancerX5: productLifeReadyFunc.onUpdateSelectedCancerX5,
        onUpdateSelectedKoomverCover: productLifeReadyFunc.onUpdateSelectedKoomverCover,
        onUpdateSelectKoomverCompensate: productLifeReadyFunc.onUpdateSelectKoomverCompensate,
        switchProductBySumAssured: productLifeReadyFunc.switchProductBySumAssured,
      },
      LIFE_SAVE_PRO: {
        switchLifeSaveProProductByBirthDate: productLifeSaveProFunc.switchLifeSaveProProductByBirthDate,
        // benefit
        calculateInvestmentBenefit: productLifeSaveProBenefitFunc.calculateInvestmentBenefit,
      },
      LOAN: {
        validateAndUpdateLoanAmount: productLoanCoverageFunc.validateAndUpdateLoanAmount,
        validateAndUpdateLoanTerm: productLoanCoverageFunc.validateAndUpdateLoanTerm,
        doValidateSumAssured: productLoanCoverageFunc.doValidateSumAssured,
        doValidateCoverageTerm: productLoanCoverageFunc.doValidateCoverageTerm,
        onUpdateInsuranceLoanTerm: productLoanCoverageFunc.onUpdateInsuranceLoanTerm,
        setProductDefaults: productLoanCoverageFunc.setProductDefaults,
        onCalculatePremium: productLoanCoverageFunc.onCalculatePremium,
        onGetDocumentListRequest: productLoanBenefitFunc.onGetDocumentListRequest,
      },
      MRTA: {
        setProductDefaultsMRTA: productMRTAFunc.setProductDefaultsMRTA,
        selectProductMRTA: productMRTAFunc.selectProductMRTA,
        generateCoveragePlanFromLoanAmountChange: productMRTAFunc.generateCoveragePlanFromLoanAmountChange,
        validateCoveragePeriodChange: productMRTAFunc.validateCoveragePeriodChange,
        validateLoanPeriodChange: productMRTAFunc.validateLoanPeriodChange,
        validateInterestRateErrors: productMRTAFunc.validateInterestRateErrors,
        resetInterestRate: productMRTAFunc.resetInterestRate,
        switchPlanMRTAProductByAge: productMRTAFunc.switchPlanMRTAProductByAge,
      },
      PA: {
        setProductDefaultsPA: productPAFunc.setProductDefaultsPA,
        calculatePremiumOptionPlanPA: riderPremiumRateService.calculatePremiumOptionPlanPA,
        getAvailableOptionPlanPARider: productPACoverageSelector.getAvaliableOptionPlanPARider,
        resolveRiderOptionForPlan: productPAFunc.resolveRiderOptionForPlan,
      },
      PERFECT_LIFE: {
        selectPerfectLifePlan: productPerfectLifeFunc.selectPerfectLifePlan,
        switchProductByBirthDate: productPerfectLifeFunc.switchProductByBirthDate,
      },
      SUKHAPABDEKDEE: {
        selectSukhapabDekDeePlan: productSukhapabdekdeeFunc.selectSukhapabDekDeePlan,
      },
      SIO: {
        onUpdateSelectedProductSIOWithCombineBoxPlanCode:
          productSIOFunc.onUpdateSelectedProductSIOWithCombineBoxPlanCode,
        switchProductSIOBySumAssured: productSIOFunc.switchProductSIOBySumAssured,
        switchPlanProductSIOByAge: productSIOFunc.switchPlanProductSIOByAge,
      },
      TERM_LIFE: {
        onUpdateSelectedTermLife: productTermLifeFunc.onUpdateSelectedTermLife,
        switchTermLifeGroupProductByAge: productTermLifeFunc.switchTermLifeGroupProductByAge,
        getSelectableProductUltimateGrowth: productTermLifeCoverageSelector.getSelectableProductUltimateGrowth,
        calculateInvestmentBenefit: productTermLifeBenefitFunc.calculateInvestmentBenefit,
      },
      WHOLE_LIFE: {
        switchProductByBirthDate: productWholeLifeFunc.switchProductByBirthDate,
        // benefit
        addOrRemoveRiderBenefits: productWholeLifeBenefitFunc.addOrRemoveRiderBenefits,
        generatePolicyValue: productWholeLifeBenefitFunc.generatePolicyValue,
      },
    },
  }
}
